import React from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { LpHeader, LpFooter, IsThisYou, LpCtaBlock, useScrollFade, fadeStyle, useLpTracking } from './LpShared';
import { trackEvent } from '../analytics';

const PAGE = '/lp/camera-partner';
const VENDORS = ['Axis', 'Hanwha', 'Hikvision', 'Bosch', 'Dahua', 'Uniview'];

export default function LpCameraPartner() {
  const navigate = useNavigate();
  useLpTracking(PAGE);
  const revenueSection = useScrollFade();
  const howSection = useScrollFade();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <LpHeader ctaLabel="Discuss a Partnership" ctaPath="/contact" pagePath={PAGE} />

      {/* ── Hero ── */}
      <Box sx={{ position: 'relative', minHeight: { xs: 520, md: 660 }, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <Box component="img" src="/ai-tech.png" alt="AI camera technology" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.18 }} />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(10,10,18,0.97) 0%, rgba(10,10,18,0.72) 55%, rgba(10,10,18,0.9) 100%)' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: { xs: 8, md: 12 }, px: 3 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.25)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#5ce1e6' }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase' }}>For Camera Manufacturers</Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.3rem', md: '3.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', lineHeight: 1.05, mb: 3 }}>
            Your hardware is getting commoditized.<br />
            <Box component="span" sx={{ color: '#5ce1e6' }}>The money is in the intelligence layer.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 580, lineHeight: 1.65, mb: 5 }}>
            SMCLA turns every camera installation into a recurring AI safety subscription — without you building anything. Your hardware. Our AI. Shared revenue.
          </Typography>
          <Box>
            <Box
              component="button"
              onClick={() => { trackEvent('cta_click', 'Discuss Camera Partnership', PAGE, { location: 'lp_hero' }); navigate('/contact'); }}
              sx={{ display: 'inline-block', px: 4, py: 1.5, fontWeight: 700, fontSize: 16, fontFamily: 'Inter, system-ui, sans-serif', bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, border: 'none', cursor: 'pointer', boxShadow: '0 0 24px rgba(92,225,230,0.3)' }}
            >
              Discuss a Camera Partnership →
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Compatible vendors ── */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 4, md: 5 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.6)', textAlign: 'center', mb: 2, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            SMCLA integrates with hardware from
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
            {VENDORS.map((v) => (
              <Box key={v} sx={{ px: 2.5, py: 0.75, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 2 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: '#9499b0' }}>{v}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Is this you? ── */}
      <Box sx={{ py: { xs: 7, md: 10 }, px: 3 }}>
        <Container maxWidth="md">
          <IsThisYou items={[
            "Camera ASPs compress every year — Chinese hardware is 40% cheaper and closing fast",
            "Your customers ask 'what else can you do?' — and the answer is increasingly just 'record'",
            "You've watched software companies build AI on top of your hardware and capture the margin",
            "Your enterprise deals stall when procurement asks about AI-powered safety monitoring",
          ]} />
        </Container>
      </Box>

      {/* ── Revenue case ── */}
      <Box ref={revenueSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 7, md: 10 }, px: 3, ...fadeStyle(revenueSection.inView) }}>
        <Container maxWidth="md">
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>The revenue case</Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 5 }}>
            Every installation becomes a recurring subscription.
          </Typography>
          <Grid container spacing={3}>
            {[
              { num: 'SaaS', label: 'Revenue share on every active SMCLA deployment', sub: 'Your cameras. Our AI. You earn recurring revenue for every facility subscription active on your hardware.', color: '#5ce1e6' },
              { num: '0', label: 'AI development cost on your side', sub: 'SMCLA handles model training, inference infrastructure, alert delivery, and updates. You distribute and integrate.', color: '#4ade80' },
              { num: '#1', label: 'First camera brand to offer native AI child safety detection', sub: 'The childcare safety AI vertical is early. First-mover advantage in this category is still available.', color: '#ff9f43' },
            ].map(({ num, label, sub, color }) => (
              <Grid key={num} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: `1px solid ${color}20`, borderRadius: 3, height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '2.2rem', color, lineHeight: 1, mb: 1 }}>{num}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.75 }}>{label}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{sub}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── How it works ── */}
      <Box ref={howSection.ref} sx={{ py: { xs: 7, md: 10 }, px: 3, ...fadeStyle(howSection.inView) }}>
        <Container maxWidth="md">
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>How the integration works</Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 5 }}>
            ONVIF, RTSP, or vendor SDK. Under 6 weeks to live.
          </Typography>
          <Stack spacing={2.5}>
            {[
              { num: '1', title: 'SMCLA connects to your camera stream', body: 'Via ONVIF, RTSP, or a vendor-specific SDK integration. No change to your camera hardware or firmware required. SMCLA ingests the stream and applies behavioral AI analysis at the edge or cloud.' },
              { num: '2', title: 'You bundle SMCLA as "Smart Safety" in your offering', body: 'Brand it as your own, or co-brand with SMCLA. You control the customer relationship. SMCLA operates as invisible infrastructure — detection, alerts, evidence packaging, and uptime are ours to manage.' },
              { num: '3', title: 'Revenue share activates automatically', body: 'For every facility where SMCLA is deployed on your hardware, you receive a revenue share — automatically, without a separate sales motion. The more you distribute, the more you earn.' },
            ].map(({ num, title, body }) => (
              <Box key={num} sx={{ display: 'flex', gap: 3, p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, alignItems: 'flex-start', '&:hover': { borderColor: 'rgba(92,225,230,0.25)' }, transition: 'border-color 0.2s' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 16, color: '#5ce1e6' }}>{num}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#f0f0f8', mb: 1 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      <LpCtaBlock
        headline="Turn your hardware into a safety platform."
        sub="We're selective about camera partners. Reach out and we'll walk you through the integration model, revenue share terms, and go-to-market approach."
        ctaLabel="Discuss a Camera Partnership"
        ctaPath="/contact"
        pagePath={PAGE}
      />
      <LpFooter />
    </Box>
  );
}
